import { OperatorId } from '../config/OperatorId';
import type { IOperator } from '../types/IOperator';

const TRAVELATA_GROUP: number[] = OperatorId.Travelata;
const CANONICAL_TRAVELATA_ID = TRAVELATA_GROUP[0];
const TRAVELATA_DISPLAY_NAME = 'Travelata';
const TRAVELATA_DISPLAY_NAME_RU = 'Травелата';
const AFFILIATE_GROUP = new Set<number>([OperatorId.Yandex]);

export function isTravelataOperatorId(id: number): boolean {
  return TRAVELATA_GROUP.includes(id);
}

export function isAffiliateOperatorId(id: number): boolean {
  return AFFILIATE_GROUP.has(id);
}

export function canonicalOperatorId(id: number): number {
  return isTravelataOperatorId(id) ? CANONICAL_TRAVELATA_ID : id;
}

/**
 * Нормализация имён оператора при создании сущности. В справочнике content имена
 * уникальны, поэтому второй ТО группы заведён как «Travelata2» — наружу вся группа
 * должна выглядеть одним брендом «Travelata».
 *
 * Применяется в конструкторах сущности оператора (Domain/Entity/Operator и
 * entities/operator/model/Operator) — через них проходят все операторы из ответов
 * поиска. Поэтому в коде отображения ничего оборачивать не нужно: operator.name
 * уже готов к показу.
 */
export function normalizeOperatorNaming<T extends { id: number }>(operator: T): T {
  if (!isTravelataOperatorId(Number(operator.id))) return operator;
  return { ...operator, name: TRAVELATA_DISPLAY_NAME, nameRu: TRAVELATA_DISPLAY_NAME_RU };
}

export function collapseOperators<T extends Pick<IOperator, 'id'>>(operators: T[]): T[] {
  let travelataAdded = false;
  return operators.reduce((acc, operator) => {
    if (isTravelataOperatorId(operator.id)) {
      if (!travelataAdded) {
        travelataAdded = true;
        // Пункт группы всегда отдаём с каноническим id: выбор в сторах канонизируется,
        // и при id 299 в справочнике галочка «Травелата» не совпала бы с v-model.
        const canonical = operators.find((item) => item.id === CANONICAL_TRAVELATA_ID);
        acc.push(canonical ?? ({ ...operator, id: CANONICAL_TRAVELATA_ID } as T));
      }
      return acc;
    }
    acc.push(operator);
    return acc;
  }, [] as T[]);
}

export function expandOperatorIds(ids: number[]): number[] {
  if (!ids.some((id) => isTravelataOperatorId(id))) return ids;
  return [...new Set([...ids, ...TRAVELATA_GROUP])];
}
